import { useMemo, useState } from 'react';
import { Download, KanbanSquare, RefreshCw, Search, Table2, Trash2, Users, Wand2 } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTableArea,
  CardTitle,
} from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { PageLoader } from '@/components/ui/Spinner';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import { triggerBlobDownload } from '@/lib/download';
import { leadStatus, leadStatusOrder } from '@/theme/status';
import type { KanbanFiltros, LeadCard, LeadsFiltros, LeadFiltros } from '@/types/crm';
import type { Channel, EstadoLead } from '@/types/domain';
import { LeadDetailDialog } from './LeadDetailDialog';
import { LeadStatusDialog } from './LeadStatusDialog';
import { LeadsKanban } from './LeadsKanban';
import { LeadsTable } from './LeadsTable';
import { RecalculateDialog } from './RecalculateDialog';
import { PurgeDialog } from './PurgeDialog';
import {
  ATTENTION_FILTERS,
  CHANNEL_FILTERS,
  KANBAN_LIMITS,
  KANBAN_LIMIT_DEFAULT,
  LEADS_LIMITS,
  LEADS_LIMIT_DEFAULT,
  attentionToBotActivo,
  countLabel,
  describeDescargaClientes,
  describeRange,
  filterLeads,
  formatNumber,
  nombreArchivoClientes,
  summarizeKanban,
  summarizeLeads,
  type AttentionFilter,
} from './crm-format';
import { useExportLeadsCsv, useKanban, useLeads } from './use-crm';

/**
 * CRM (`/crm`, módulo 9): el embudo de leads del negocio activo.
 *
 * El embudo tiene 4 estados (Frío → Tibio → Caliente → Cliente) y el módulo se lee de dos formas,
 * con las mismas columnas del backend:
 *  - KANBAN (`GET .../crm/kanban`): las 4 columnas con las tarjetas más recientes de cada estado. NO
 *    pagina: cada columna trae hasta `limitePorColumna` tarjetas (default 30, máx. 200) y el total
 *    real, que la pantalla muestra cuando es mayor;
 *  - TABLA (`GET .../crm/leads`): el listado paginado (`limite` + `offset`) con los totales por
 *    estado, para revisar la lista completa o buscar un contacto.
 *
 * Lo que la barra de herramientas manda al SERVIDOR es lo que forma parte del contrato (`estadoLead`,
 * `channel`, `botActivo` y el límite); el buscador de texto filtra en el cliente sobre lo cargado,
 * porque el `buscar` del endpoint solo compara contra el identificador de contacto y en esta pantalla
 * se busca también por motivo, campaña o el cliente de la última orden.
 *
 * Permisos: ver el CRM y reclasificar un lead a mano lo puede hacer CUALQUIER miembro (el
 * AGENTE_HUMANO atiende chats); el recálculo masivo es solo ADMIN (`canEdit`, espejo de
 * `requireRole('ADMIN')`), así que su botón solo aparece para el admin. Lo mismo pasa con las otras
 * dos acciones de administrador: descargar la lista de clientes en CSV y borrar conversaciones viejas.
 */

/** Vista activa de la pantalla. */
type CrmView = 'kanban' | 'tabla';

/** Filtros que ve el usuario en la barra (`''` = sin filtrar). */
interface CrmFiltersState {
  search: string;
  estadoLead: EstadoLead | '';
  channel: Channel | '';
  attention: AttentionFilter;
}


/**
 * Descarga de la lista de clientes en CSV (`GET .../crm/leads/export.csv`, solo ADMIN).
 *
 * Atención a la diferencia entre lo que se VE y lo que se DESCARGA: el archivo trae TODOS los leads
 * que cumplen los filtros que están en la barra (estado, canal y atención del bot), sin paginar y sin
 * el buscador de texto —ese filtra en el cliente, sobre lo que ya está cargado—. O sea: la pantalla
 * muestra una página del listado o una columna del tablero, y el CSV trae el embudo completo.
 *
 * El archivo no puede ser un enlace: el endpoint pide el `Authorization` de la sesión, así que se
 * baja con `fetch` y lo guarda `triggerBlobDownload`, con un nombre que arma el panel a partir del
 * nombre del negocio (`nombreArchivoClientes`).
 */
export interface CrmExportButtonProps {
  /** Los mismos filtros que se le mandan al kanban y al listado. */
  filters: LeadFiltros;
  /** Nombre del negocio activo, para el nombre del archivo. */
  nombreEmpresa: string | null;
}

export function CrmExportButton({ filters, nombreEmpresa }: CrmExportButtonProps) {
  const { tenantId } = useActiveTenantAccess();
  const exportLeads = useExportLeadsCsv(tenantId);

  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  async function handleExport() {
    setError(null);
    setAviso(null);

    try {
      const descarga = await exportLeads.mutateAsync(filters);
      triggerBlobDownload(descarga.blob, nombreArchivoClientes(nombreEmpresa));
      setAviso(describeDescargaClientes(descarga));
    } catch (err) {
      setError(getErrorMessage(err));
    }
  }

  return (
    <div className="flex flex-col items-stretch gap-2 sm:items-end">
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<Download size={14} />}
        loading={exportLeads.isPending}
        aria-label="Descargar la lista de clientes en CSV"
        onClick={() => void handleExport()}
      >
        Exportar CSV
      </Button>

      {error ? <Alert tone="danger">{error}</Alert> : null}

      {aviso ? (
        <p className="text-micro text-ink-faint" role="status">
          {aviso}
        </p>
      ) : null}
    </div>
  );
}

/**
 * El tablero (`useKanban`) con sus estados de carga, error y vacío.
 *
 * El filtro de texto NO viaja al backend (ver la cabecera de la pantalla): se aplica acá sobre las
 * tarjetas que ya llegaron, columna por columna; el conteo de cada columna sigue mostrando el total
 * real del servidor, que es justo lo que avisa que hay más.
 */
export interface CrmKanbanSectionProps {
  /** Filtros que viajan al endpoint (los mismos que forman la query key). */
  filters: KanbanFiltros;
  /** Búsqueda del cliente (se aplica a las tarjetas ya cargadas). */
  search: string;
  onOpenLead: (lead: LeadCard) => void;
  onStatusLead: (lead: LeadCard) => void;
}

export function CrmKanbanSection({
  filters,
  search,
  onOpenLead,
  onStatusLead,
}: CrmKanbanSectionProps) {
  const { tenantId } = useActiveTenantAccess();
  const query = useKanban(tenantId, filters);

  if (query.isPending) return <PageLoader label="Cargando el tablero de leads…" />;

  if (query.isError) return <Alert>{getErrorMessage(query.error)}</Alert>;

  const data = query.data;
  if (!data) return null;

  const kanban = {
    ...data,
    columnas: data.columnas.map((columna) => ({
      ...columna,
      items: filterLeads(columna.items, search),
    })),
  };

  const visibles = kanban.columnas.reduce((total, columna) => total + columna.items.length, 0);

  if (data.total === 0) {
    return (
      <EmptyState
        icon={<Users size={22} />}
        title="Todavía no hay leads"
        description="El CRM se llena solo: cada conversación que entra por un canal es un lead, y el clasificador le pone estado (Frío, Tibio, Caliente o Cliente) según lo que el cliente escribe. Si acabas de activar el CRM, corre el recálculo para clasificar los chats que ya existían."
      />
    );
  }

  if (visibles === 0) {
    return (
      <EmptyState
        icon={<Search size={22} />}
        title="Ningún lead coincide"
        description={`Nada en el tablero coincide con «${search.trim()}». El buscador mira el contacto, el motivo, la campaña y el último mensaje de lo que ya está cargado.`}
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-caption text-ink-muted">
          {search.trim().length > 0
            ? `${countLabel(visibles, 'tarjeta visible', 'tarjetas visibles')} de ${formatNumber(
                data.total,
              )}.`
            : summarizeKanban(data)}
        </p>

        <Button
          variant="ghost"
          size="sm"
          leftIcon={<RefreshCw size={14} />}
          loading={query.isFetching}
          onClick={() => void query.refetch()}
        >
          Actualizar
        </Button>
      </div>

      <LeadsKanban kanban={kanban} onOpenLead={onOpenLead} onStatusLead={onStatusLead} />
    </div>
  );
}

/**
 * La tabla (`useLeads`): el listado paginado, con el total real por estado.
 *
 * El backend pagina de verdad (`limite` + `offset`) y devuelve `totalesPorEstado`, así que la
 * pantalla puede decir «Mostrando 26-50 de 120 leads» sin inventar nada. La paginación se controla
 * con `onOffset` (el estado vive en la página, para que sobreviva al cambio de vista).
 */
export interface CrmTableSectionProps {
  /** Filtros que viajan al endpoint, incluidos `limite` y `offset`. */
  filters: LeadsFiltros;
  /** Búsqueda del cliente (se aplica a las filas ya cargadas). */
  search: string;
  onOpenLead: (lead: LeadCard) => void;
  onStatusLead: (lead: LeadCard) => void;
  /** Cambia de página. */
  onOffset: (offset: number) => void;
}

export function CrmTableSection({
  filters,
  search,
  onOpenLead,
  onStatusLead,
  onOffset,
}: CrmTableSectionProps) {
  const { tenantId } = useActiveTenantAccess();
  const query = useLeads(tenantId, filters);

  if (query.isPending) return <PageLoader label="Cargando los leads…" />;

  if (query.isError) return <Alert>{getErrorMessage(query.error)}</Alert>;

  const data = query.data;
  if (!data) return null;

  const visibles = filterLeads(data.items, search);
  const limite = filters.limite ?? data.limite;
  const offset = filters.offset ?? data.offset;
  const desde = offset + 1;
  const hasta = offset + data.items.length;
  const hayAnterior = offset > 0;
  const haySiguiente = hasta < data.total;

  if (data.total === 0) {
    return (
      <EmptyState
        icon={<Users size={22} />}
        title="Todavía no hay leads"
        description="Cada conversación que entra por un canal es un lead. Cuando el cliente escriba, el clasificador le pone estado y aparece acá."
      />
    );
  }

  if (data.items.length === 0) {
    return (
      <EmptyState
        icon={<Users size={22} />}
        title="Esta página quedó vacía"
        description={`El listado tiene ${countLabel(
          data.total,
          'lead',
          'leads',
        )} y esta página (desde el ${formatNumber(offset + 1)}) no trajo ninguno: vuelve al principio para verlos.`}
        action={<Button onClick={() => onOffset(0)}>Volver a la primera página</Button>}
      />
    );
  }

  if (visibles.length === 0) {
    return (
      <EmptyState
        icon={<Search size={22} />}
        title="Ningún lead coincide"
        description={`Nada en esta página coincide con «${search.trim()}». El buscador solo mira las ${formatNumber(
          data.items.length,
        )} filas cargadas: limpia la búsqueda o pasa de página.`}
        action={
          <Button variant="secondary" onClick={() => onOffset(0)}>
            Volver a la primera página
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-caption text-ink-muted">{summarizeLeads(data)}</p>

        <Button
          variant="ghost"
          size="sm"
          leftIcon={<RefreshCw size={14} />}
          loading={query.isFetching}
          onClick={() => void query.refetch()}
        >
          Actualizar
        </Button>
      </div>

      <CardTableArea>
        <LeadsTable leads={visibles} onOpen={onOpenLead} onStatus={onStatusLead} />
      </CardTableArea>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-caption text-ink-faint">
          {describeRange(offset, data.items.length, data.total)}
          {search.trim().length > 0
            ? ` · ${countLabel(visibles.length, 'fila visible', 'filas visibles')} en esta página`
            : ''}
        </p>

        <span className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={!hayAnterior}
            onClick={() => onOffset(Math.max(0, offset - limite))}
          >
            Anterior
          </Button>

          <Button
            variant="secondary"
            size="sm"
            disabled={!haySiguiente}
            onClick={() => onOffset(offset + limite)}
          >
            Siguiente
          </Button>
        </span>
      </div>
    </div>
  );
}


export function CrmPage() {
  const { tenantId, nombreEmpresa, canEdit } = useActiveTenantAccess();

  const [view, setView] = useState<CrmView>('kanban');
  const [search, setSearch] = useState('');
  const [estadoLead, setEstadoLead] = useState<EstadoLead | ''>('');
  const [channel, setChannel] = useState<Channel | ''>('');
  const [attention, setAttention] = useState<AttentionFilter>('');
  const [kanbanLimit, setKanbanLimit] = useState<number>(KANBAN_LIMIT_DEFAULT);
  const [leadsLimit, setLeadsLimit] = useState<number>(LEADS_LIMIT_DEFAULT);
  const [offset, setOffset] = useState(0);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [statusLead, setStatusLead] = useState<LeadCard | null>(null);
  const [recalcOpen, setRecalcOpen] = useState(false);
  /** Purga de conversaciones viejas (solo ADMIN): su propio diálogo, con simulación previa. */
  const [purgeOpen, setPurgeOpen] = useState(false);

  /**
   * Filtros que viajan al backend (los mismos que forman la query key). Cambiar cualquiera devuelve
   * la tabla a la primera página: un `offset` viejo sobre otro filtro no significa nada.
   */
  const baseFilters = useMemo<LeadFiltros>(
    () => ({
      ...(estadoLead ? { estadoLead } : {}),
      ...(channel ? { channel } : {}),
      ...(attention ? { botActivo: attentionToBotActivo(attention) } : {}),
    }),
    [estadoLead, channel, attention],
  );

  const kanbanFilters = useMemo<KanbanFiltros>(
    () => ({ ...baseFilters, limitePorColumna: kanbanLimit }),
    [baseFilters, kanbanLimit],
  );

  const leadsFilters = useMemo<LeadsFiltros>(
    () => ({ ...baseFilters, limite: leadsLimit, offset }),
    [baseFilters, leadsLimit, offset],
  );

  const filtering =
    search.trim().length > 0 || estadoLead !== '' || channel !== '' || attention !== '';

  function resetOffset() {
    setOffset(0);
  }

  function clearFilters() {
    setSearch('');
    setEstadoLead('');
    setChannel('');
    setAttention('');
    resetOffset();
  }

  /** El tablero y la tabla comparten el manejador: abren la ficha o la reclasificación. */
  const openLead = (lead: LeadCard) => setDetailId(lead.conversationId);
  const openStatus = (lead: LeadCard) => setStatusLead(lead);

  return (
    <>
      <Card>
        <CardHeader
          actions={
            canEdit ? (
              <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-start">
                <CrmExportButton filters={baseFilters} nombreEmpresa={nombreEmpresa} />

                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Wand2 size={14} />}
                  onClick={() => setRecalcOpen(true)}
                >
                  Recalcular leads
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Trash2 size={14} />}
                  onClick={() => setPurgeOpen(true)}
                >
                  Borrar viejas
                </Button>
              </div>
            ) : null
          }
        >
          <CardTitle>CRM</CardTitle>
          <CardDescription>
            El embudo de leads del negocio: Frío → Tibio → Caliente → Cliente, con el por qué de cada
            estado (las señales del chat, el historial y las órdenes del contacto). Reclasificar un
            lead a mano lo puede hacer cualquiera del equipo; recalcular todos los leads de una vez,
            solo el administrador (igual que descargar la lista de clientes en CSV).
            {canEdit
              ? ''
              : ' Tu rol en este negocio no permite el recálculo masivo: los botones de la tabla y del tablero sí funcionan (mueven un lead a la vez).'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <Input
              type="search"
              value={search}
              leftIcon={<Search size={16} />}
              placeholder="Buscar por contacto, motivo, campaña o último mensaje…"
              aria-label="Buscar leads"
              onChange={(event) => setSearch(event.target.value)}
            />

            <Select
              aria-label="Filtrar por estado del lead"
              className="lg:w-48"
              value={estadoLead}
              onChange={(event) => {
                setEstadoLead(event.target.value as EstadoLead | '');
                resetOffset();
              }}
            >
              <option value="">Todos los estados</option>
              {leadStatusOrder.map((item) => (
                <option key={item} value={item}>
                  {leadStatus[item].label}
                </option>
              ))}
            </Select>

            <Select
              aria-label="Filtrar por canal"
              className="lg:w-44"
              value={channel}
              onChange={(event) => {
                setChannel(event.target.value as Channel | '');
                resetOffset();
              }}
            >
              {CHANNEL_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>

            <Select
              aria-label="Filtrar por atención del bot"
              className="lg:w-56"
              value={attention}
              onChange={(event) => {
                setAttention(event.target.value as AttentionFilter);
                resetOffset();
              }}
            >
              {ATTENTION_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>

          {view === 'kanban' && estadoLead !== '' ? (
            <p className="text-caption text-ink-faint">
              {`Con el filtro de estado, el tablero solo trae la columna «${
                leadStatus[estadoLead].label
              }»: así lo devuelve el endpoint del kanban.`}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="flex items-center gap-1" role="group" aria-label="Cambiar de vista">
              <Button
                variant={view === 'kanban' ? 'subtle' : 'ghost'}
                size="sm"
                leftIcon={<KanbanSquare size={14} />}
                aria-pressed={view === 'kanban'}
                onClick={() => setView('kanban')}
              >
                Tablero
              </Button>

              <Button
                variant={view === 'tabla' ? 'subtle' : 'ghost'}
                size="sm"
                leftIcon={<Table2 size={14} />}
                aria-pressed={view === 'tabla'}
                onClick={() => setView('tabla')}
              >
                Tabla
              </Button>
            </span>

            <span className="flex flex-wrap items-center gap-2">
              {filtering ? (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  Quitar los filtros
                </Button>
              ) : null}

              <Select
                aria-label={view === 'kanban' ? 'Tarjetas por columna' : 'Leads por página'}
                className="w-44"
                value={String(view === 'kanban' ? kanbanLimit : leadsLimit)}
                onChange={(event) => {
                  const value = Number(event.target.value);
                  if (view === 'kanban') setKanbanLimit(value);
                  else setLeadsLimit(value);
                  resetOffset();
                }}
              >
                {(view === 'kanban' ? KANBAN_LIMITS : LEADS_LIMITS).map((option) => (
                  <option key={option} value={option}>
                    {view === 'kanban' ? `${option} por columna` : `${option} por página`}
                  </option>
                ))}
              </Select>
            </span>
          </div>

          {view === 'kanban' ? (
            <CrmKanbanSection
              filters={kanbanFilters}
              search={search}
              onOpenLead={openLead}
              onStatusLead={openStatus}
            />
          ) : (
            <CrmTableSection
              filters={leadsFilters}
              search={search}
              onOpenLead={openLead}
              onStatusLead={openStatus}
              onOffset={(next) => setOffset(Math.max(0, next))}
            />
          )}
        </CardContent>
      </Card>

      {/* La ficha se abre desde cualquier tarjeta o fila; «Reclasificar a mano» cierra la ficha y abre
          el diálogo de estado con el mismo lead (que es el `LeadCard` que devolvió el detalle). */}
      <LeadDetailDialog
        open={detailId !== null}
        conversationId={detailId}
        onClose={() => setDetailId(null)}
        onStatus={(lead) => {
          setDetailId(null);
          setStatusLead(lead);
        }}
      />

      <LeadStatusDialog
        open={statusLead !== null}
        lead={statusLead}
        onClose={() => setStatusLead(null)}
      />

      {/* El recálculo masivo es solo ADMIN (el backend responde 403 a cualquier otro rol). */}
      {canEdit ? (
        <RecalculateDialog open={recalcOpen} onClose={() => setRecalcOpen(false)} />
      ) : null}

      {/* La purga también es solo ADMIN: borra conversaciones del negocio entero. */}
      {canEdit ? <PurgeDialog open={purgeOpen} onClose={() => setPurgeOpen(false)} /> : null}
    </>
  );
}

